import type { IubendaCookie } from '@/types'

// Listens for the iubendaCookieSet event dispatched from GTM which indicates the cookie is fully formed
const waitForIubendaCookieEvent = (): Promise<void> => {
  const TIMEOUT_MS = 20000

  return new Promise((resolve, reject) => {
    const handler = () => {
      clearTimeout(timer)
      resolve()
    }

    const timer = setTimeout(() => {
      document.removeEventListener('iubendaCookieSet', handler)
      reject(new Error('Timeout waiting for iubendaCookieSet event'))
    }, TIMEOUT_MS)

    document.addEventListener('iubendaCookieSet', handler, { once: true })
  })
}

// Retrieve cookie by value and parse as an object
const getCookie = (name: string): IubendaCookie | undefined => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))

  if (!match) return undefined

  const decodedValue = decodeURIComponent(match.split('=')[1])
  try {
    return JSON.parse(decodedValue)
  } catch (e) {
    return undefined
  }
}

// Return iubenda consent status
export async function isMeasurementConsentGiven (): Promise<boolean> {
  const IUBENDA_COOKIE_NAME = '_iub_cs-s3997868'
  const IUBENDA_MEASUREMENT_PURPOSE = '4'

  // If GTM is disabled, return false immediately since iubenda won't be initialized
  if (import.meta.env.VITE_APP_GTM_ENABLED !== 'true') {
    return false
  }

  /**
   * Iubenda is set up to assign a cookie with "purposes" in the object to indicate
   * the preference granularity of the user based on what compliance is required in their country.
   * 
   * To check if a user is from a country which requires compliance, we need to check cookie['purposes'] which
   * will be set to a boolean for each purpose.
   * 
   * If an existing cookie is found from prior consent, return the value of the measurement (4) purpose in the cookie
   * Otherwise, wait for the iubendaCookieSet event from GTM before reading the cookie
   */
  const existingCookie = getCookie(IUBENDA_COOKIE_NAME)
  if (existingCookie?.purposes) {
    return existingCookie.purposes[IUBENDA_MEASUREMENT_PURPOSE]
  }

  return waitForIubendaCookieEvent()
    .then(() => {
      const cookieObj = getCookie(IUBENDA_COOKIE_NAME)
      return cookieObj?.purposes?.[IUBENDA_MEASUREMENT_PURPOSE] ?? false
    })
    .catch(() => {
      console.warn('Failed to receive iubendaCookieSet event')
      return false
    })
}
